import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './blocks/ModernBlocks.css';

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const [serviceData, setServiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/data/services/${serviceSlug}.json`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Service not found');
        }
        return response.json();
      })
      .then(data => {
        setServiceData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading service data:', error);
        setError(error.message);
        setLoading(false);
      });
  }, [serviceSlug]);

  const renderHeroSection = (hero) => (
    <section className="service-hero-section" style={{
      backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${hero.background})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      color: 'white'
    }}>
      <div className="service-hero-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px',
        textAlign: 'center'
      }}>
        <h1 className="service-hero-title" style={{
          fontSize: '3.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem'
        }}>{hero.title}</h1>
        <p className="service-hero-subtitle" style={{
          fontSize: '1.25rem',
          marginBottom: '2rem',
          maxWidth: '600px',
          margin: '0 auto 2rem'
        }}>{hero.subtitle}</p>
        <div className="service-hero-features" style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '1rem'
        }}>
          {hero.features.map((feature, index) => (
            <span key={index} style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '2rem',
              fontSize: '0.875rem'
            }}>{feature}</span>
          ))}
        </div>
      </div>
    </section>
  );

  const renderOverviewSection = (overview) => (
    <section className="service-overview-section" style={{
      padding: '4rem 0',
      backgroundColor: 'white'
    }}>
      <div className="service-overview-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>{overview.title}</h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280',
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>{overview.description}</p>
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '1rem'
        }}>
          {overview.technologies.map((tech, index) => (
            <span key={index} style={{
              background: '#f3f4f6',
              color: '#374151',
              padding: '0.5rem 1rem',
              borderRadius: '1rem',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>{tech}</span>
          ))}
        </div>
      </div>
    </section>
  );

  const renderServicesSection = (services) => (
    <section className="service-services-section" style={{
      padding: '4rem 0',
      backgroundColor: '#f9fafb'
    }}>
      <div className="service-services-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>Our Services</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {services.map((service, index) => (
            <div key={index} style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease'
            }} onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                marginBottom: '1.5rem',
                backgroundColor: service.color + '20',
                color: service.color
              }}>{service.icon}</div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '1rem'
              }}>{service.title}</h3>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                marginBottom: '1.5rem'
              }}>{service.description}</p>
              <ul style={{
                listStyle: 'none',
                padding: '0'
              }}>
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} style={{
                    color: '#6b7280',
                    padding: '0.25rem 0',
                    position: 'relative',
                    paddingLeft: '1.5rem'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: '0',
                      color: '#10b981',
                      fontWeight: 'bold'
                    }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderProcessSection = (process) => (
    <section className="service-process-section" style={{
      padding: '4rem 0',
      backgroundColor: 'white'
    }}>
      <div className="service-process-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>{process.title}</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem'
        }}>
          {process.steps.map((step, index) => (
            <div key={index} style={{
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#2563eb',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                margin: '0 auto 1.5rem'
              }}>{step.step}</div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '1rem'
              }}>{step.title}</h3>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                marginBottom: '1rem'
              }}>{step.description}</p>
              <ul style={{
                listStyle: 'none',
                padding: '0',
                fontSize: '0.875rem'
              }}>
                {step.deliverables.map((deliverable, deliverableIndex) => (
                  <li key={deliverableIndex} style={{
                    color: '#6b7280',
                    padding: '0.25rem 0'
                  }}>• {deliverable}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderTechnologiesSection = (technologies) => (
    <section className="service-technologies-section" style={{
      padding: '4rem 0',
      backgroundColor: '#f9fafb'
    }}>
      <div className="service-technologies-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>{technologies.title}</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem'
        }}>
          {technologies.categories.map((category, index) => (
            <div key={index} style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '1rem'
              }}>{category.name}</h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                {category.technologies.map((tech, techIndex) => (
                  <span key={techIndex} style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>{tech}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderPricingSection = (pricing) => (
    <section className="service-pricing-section" style={{
      padding: '4rem 0',
      backgroundColor: 'white'
    }}>
      <div className="service-pricing-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>{pricing.title}</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {pricing.plans.map((plan, index) => (
            <div key={index} style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '1rem',
              padding: '2rem',
              textAlign: 'center',
              transition: 'transform 0.3s ease'
            }} onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '1rem'
              }}>{plan.name}</h3>
              <div style={{
                marginBottom: '1rem'
              }}>
                <span style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#2563eb'
                }}>{plan.price}</span>
                <span style={{
                  color: '#6b7280',
                  fontSize: '1rem'
                }}> / {plan.duration}</span>
              </div>
              <ul style={{
                listStyle: 'none',
                padding: '0',
                marginBottom: '2rem'
              }}>
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} style={{
                    padding: '0.5rem 0',
                    color: '#374151'
                  }}>
                    <span style={{
                      color: '#10b981',
                      marginRight: '0.5rem'
                    }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button style={{
                width: '100%',
                padding: '1rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.3s ease'
              }} onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
                  onMouseLeave={(e) => e.target.style.background = '#2563eb'}>
                Get Started
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCaseStudiesSection = (caseStudies) => (
    <section className="service-case-studies-section" style={{
      padding: '4rem 0',
      backgroundColor: '#f9fafb'
    }}>
      <div className="service-case-studies-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>Case Studies</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem'
        }}>
          {caseStudies.map((study, index) => (
            <div key={index} style={{
              background: 'white',
              borderRadius: '1rem',
              overflow: 'hidden',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease'
            }} onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
              <div style={{
                height: '200px',
                overflow: 'hidden'
              }}>
                <img src={study.image} alt={study.title} style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }} />
              </div>
              <div style={{
                padding: '2rem'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '1rem'
                }}>{study.title}</h3>
                <p style={{
                  color: '#6b7280',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>{study.description}</p>
                <div style={{
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '0.5rem'
                  }}>Key Results:</h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: '0'
                  }}>
                    {study.results.map((result, resultIndex) => (
                      <li key={resultIndex} style={{
                        color: '#6b7280',
                        padding: '0.25rem 0',
                        position: 'relative',
                        paddingLeft: '1.5rem'
                      }}>
                        <span style={{
                          position: 'absolute',
                          left: '0',
                          color: '#10b981',
                          fontWeight: 'bold'
                        }}>→</span>
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '0.5rem'
                  }}>Technologies:</h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {study.technologies.map((tech, techIndex) => (
                      <span key={techIndex} style={{
                        background: '#f3f4f6',
                        color: '#374151',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCTASection = (cta) => (
    <section className="service-cta-section" style={{
      padding: '4rem 0',
      backgroundColor: '#1f2937',
      color: 'white',
      textAlign: 'center'
    }}>
      <div className="service-cta-container" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem'
        }}>{cta.title}</h2>
        <p style={{
          fontSize: '1.125rem',
          marginBottom: '2rem',
          opacity: '0.9'
        }}>{cta.subtitle}</p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {cta.buttons.map((button, index) => (
            <button key={index} style={{
              padding: '1rem 2rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              cursor: 'pointer',
              border: 'none',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              ...(button.variant === 'primary' ? {
                background: '#2563eb',
                color: 'white'
              } : {
                background: 'transparent',
                color: 'white',
                border: '2px solid white'
              })
            }} onMouseEnter={(e) => {
              if (button.variant === 'primary') {
                e.target.style.background = '#1d4ed8';
              } else {
                e.target.style.background = 'white';
                e.target.style.color = '#1f2937';
              }
            }} onMouseLeave={(e) => {
              if (button.variant === 'primary') {
                e.target.style.background = '#2563eb';
              } else {
                e.target.style.background = 'transparent';
                e.target.style.color = 'white';
              }
            }}>
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading Service...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Service not found: {error}
      </div>
    );
  }

  if (!serviceData) {
    return null;
  }

  return (
    <div className="service-page">
      {serviceData.hero && renderHeroSection(serviceData.hero)}
      {serviceData.overview && renderOverviewSection(serviceData.overview)}
      {serviceData.services && renderServicesSection(serviceData.services)}
      {serviceData.process && renderProcessSection(serviceData.process)}
      {serviceData.technologies && renderTechnologiesSection(serviceData.technologies)}
      {serviceData.pricing && renderPricingSection(serviceData.pricing)}
      {serviceData.caseStudies && renderCaseStudiesSection(serviceData.caseStudies)}
      {serviceData.cta && renderCTASection(serviceData.cta)}
    </div>
  );
};

export default ServicePage; 